import { useContext, useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Script from 'next/script';
import get from 'lodash/get';
import isClient from 'helpers/isClient';
import getHeadingSkin from 'helpers/getHeadingSkin';
import getHeadingTag from 'helpers/getHeadingTag';
import { getClientLocalePrefix } from 'helpers/getLocalePrefix';
import { Footer } from 'components/organisms/Footer';
import Container from 'components/layout/Grid/Container';
import Column from 'components/layout/Grid/Column';
import { DonateFormProps } from './DonateForm.types';
import {
  WrapperStyled,
  DonateHeaderStyled,
  RowButtonStyled,
  LinkStyled,
  Icon,
  RowHeadlineStyled,
  HeadingStyled,
  SubHeadlineStyled,
  DonateFormCSS,
} from './DonateForm.styles';
import { DomainContext } from 'contexts/domain/DomainContext';
import { DEFAULT_DOMAIN } from 'prismic/config';

const mapLocale = (locale: string): 'de' | 'fr' | 'it' | 'en' => {
  if (locale === 'it') {
    return 'it';
  }
  if (locale === 'en') {
    return 'en';
  }
  if (locale === 'de' || locale === 'fr') {
    return locale;
  }

  return 'de';
};

let initInterval: ReturnType<typeof setTimeout> | null = null;

export const DonateForm: React.FC<DonateFormProps> = ({ footer }) => {
  const { t } = useTranslation('common');
  const locale = getClientLocalePrefix();
  const {
    state: { domain },
  } = useContext(DomainContext);

  const headline =
    domain === DEFAULT_DOMAIN ? 'donate.ballenberg.headline' : 'donate.ballenbergkurse.headline';
  const subHeadline =
    domain === DEFAULT_DOMAIN
      ? 'donate.ballenberg.subHeadline'
      : 'donate.ballenbergkurse.subHeadline';
  const backGroundImage =
    domain === DEFAULT_DOMAIN
      ? '/donate/donate-header-background.jpg'
      : '/donate/donate-header-background-KBB.jpg';
  const linkRaiseNow =
    domain === DEFAULT_DOMAIN
      ? 'https://tamaro.raisenow.com/ballenberg-ch/latest/widget.js'
      : 'https://tamaro.raisenow.com/verei-f700/latest/widget.js';

  const initDonateWidget = () => {
    // @ts-ignore
    if (isClient() && window && window.rnw) {
      const formLanguage = mapLocale(locale);

      // @ts-ignore
      window.rnw.tamaro.runWidget('.rnw-widget-container', {
        language: formLanguage,
        testMode: false,
        paymentWidgetBlocks: [
          'payment_amounts_and_intervals',
          'payment_payment_methods',
          'payment_profile',
          'payment_address',
        ],
        paymentValidations: {
          amount: {
            minimum_custom_amount: true,
            maximum_custom_amount: true,
          },
          stored_customer_salutation: {
            required: true,
          },
          stored_customer_firstname: {
            required: true,
          },
          stored_customer_lastname: {
            required: true,
          },
          stored_customer_email: {
            required: true,
            email: true,
          },
          stored_customer_birthdate: {
            required: true,
            date_format: true,
            age: {
              min: 18,
              max: 100,
            },
          },
          stored_customer_street: {
            required: true,
          },
          stored_customer_zip_code: {
            required: true,
          },
          stored_customer_city: {
            required: true,
          },
          stored_customer_country: {
            required: true,
          },
          stored_customer_state: {
            required: true,
          },
          cardno: {
            credit_card_type_supported: true,
          },
        },
      });
      if (initInterval) {
        clearInterval(initInterval);
        initInterval = null;
      }
    }
  };

  useEffect(() => {
    if (!initInterval) {
      initInterval = setInterval(() => {
        initDonateWidget();
      }, 100);
    }
  }, []);

  return (
    <WrapperStyled>
      <DonateFormCSS />
      <DonateHeaderStyled style={{ backgroundImage: `url(${backGroundImage})` }}>
        <RowButtonStyled>
          <LinkStyled rel="noreferrer" href={`/${getClientLocalePrefix()}`}>
            <p>{t('links.close')}</p>
            <Icon viewBox="0 0 20 20">
              <line x1={1} x2={19} y1={6} y2={6} />
              <line x1={1} x2={19} y1={14} y2={14} />
            </Icon>
          </LinkStyled>
        </RowButtonStyled>
        <RowHeadlineStyled>
          <HeadingStyled skin={getHeadingSkin('heading1')} elementType={getHeadingTag('heading1')}>
            {t(headline)}
          </HeadingStyled>
          <SubHeadlineStyled>{t(subHeadline)}</SubHeadlineStyled>
        </RowHeadlineStyled>
      </DonateHeaderStyled>
      <Container>
        <Column span={12}>
          <div className="rnw-widget-container"></div>
        </Column>
      </Container>
      <Footer data={get(footer, 'data.body', [])} />
      <Script src={linkRaiseNow}></Script>
    </WrapperStyled>
  );
};
